// src/pages/AllVendingMachines.jsx  (or wherever you place it)

import React, { useState, useEffect } from 'react';
import { FaPlus, FaSearch, FaTimes, FaEdit } from 'react-icons/fa';
import { motion } from 'framer-motion';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle, MapPin, Hash } from 'lucide-react';
import SummaryApi from '../common'; // ← your API config file

const AllVendingMachines = () => {
  const [machines, setMachines] = useState([]);
  const [filteredMachines, setFilteredMachines] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMachine, setEditMachine] = useState(null);

  useEffect(() => {
    fetchMachines();
  }, []);

  useEffect(() => {
    if (modalOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalOpen]);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const res = await fetch(SummaryApi.VendingMeching.url, {
        method: SummaryApi.VendingMeching.method,
        headers: { 'Content-Type': 'application/json' },
      });

      const result = await res.json();

      if (result.status === true && Array.isArray(result.data)) {
        setMachines(result.data);
        setFilteredMachines(result.data);
      } else {
        toast.error(result.message || 'Failed to load vending machines');
      }
    } catch (err) {
      console.error('Fetch machines error:', err);
      toast.error('Cannot connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Real-time search filter
  useEffect(() => {
    const term = search.toLowerCase().trim();
    if (!term) {
      setFilteredMachines(machines);
      return;
    }

    const filtered = machines.filter((m) =>
      [m.vending_machine_name, m.serial_number, m.location, m.address || '']
        .some((val) => (val || '').toLowerCase().includes(term))
    );

    setFilteredMachines(filtered);
  }, [search, machines]);

  const openModal = (machine = null) => {
    setEditMachine(machine);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setEditMachine(null), 300); // slight delay for exit animation
  };

  const showSuccess = (title, msg) => {
    toast.custom(
      () => (
        <motion.div
          initial={{ y: 60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="max-w-md w-full bg-white shadow-2xl rounded-3xl border-b-4 border-green-600 overflow-hidden"
        >
          <div className="p-5 flex items-start gap-4">
            <div className="h-12 w-12 rounded-2xl bg-green-100 flex items-center justify-center text-green-600">
              <CheckCircle size={28} />
            </div>
            <div>
              <p className="font-bold text-gray-900">{title}</p>
              <p className="text-sm text-gray-600 mt-1">{msg}</p>
            </div>
          </div>
        </motion.div>
      ),
      { duration: 3800, position: 'top-center' }
    );
  };

  // ────────────────────────────────────────────────
  //                   Machine Card
  // ────────────────────────────────────────────────
  const MachineCard = ({ machine }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, scale: 1.025 }}
      transition={{ type: 'spring', stiffness: 300, damping: 22 }}
      className="group bg-white rounded-2xl shadow-md hover:shadow-2xl border border-gray-100/80 overflow-hidden flex flex-col h-full transition-all duration-300"
    >
      <div className="h-48 bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 relative flex items-center justify-center">
        <div className="text-8xl opacity-20 group-hover:opacity-40 transition-opacity duration-500">🛒</div>

        <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
          <span
            className={`px-3.5 py-1 rounded-full text-xs font-semibold shadow-sm ${
              machine.is_live ? 'bg-green-100 text-green-800 border-green-200' : 'bg-amber-100 text-amber-800 border-amber-200'
            }`}
          >
            {machine.is_live ? 'LIVE' : 'OFFLINE'}
          </span>
          <span
            className={`px-3.5 py-1 rounded-full text-xs font-semibold shadow-sm ${
              machine.is_active ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-rose-100 text-rose-800 border-rose-200'
            }`}
          >
            {machine.is_active ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
      </div>

      <div className="p-6 flex flex-col flex-grow">
        <h3 className="font-bold text-xl text-gray-900 mb-3 line-clamp-2 group-hover:text-indigo-700 transition-colors">
          {machine.vending_machine_name || 'Unnamed Machine'}
        </h3>

        <div className="space-y-3 text-sm text-gray-600 mb-6 flex-grow">
          <div className="flex items-center gap-2.5">
            <Hash size={16} className="text-gray-400 flex-shrink-0" />
            <span className="font-medium">Serial:</span>
            <span className="text-gray-700 truncate">{machine.serial_number || '—'}</span>
          </div>

          <div className="flex items-start gap-2.5">
            <MapPin size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Location:</span>
              <p className="text-gray-700 line-clamp-2">{machine.location || '—'}</p>
            </div>
          </div>

          {machine.address && (
            <p className="text-gray-500 text-xs pl-6 line-clamp-2 italic">
              {machine.address}
            </p>
          )}
        </div>

        <button
          onClick={() => openModal(machine)}
          className="mt-auto w-full py-3.5 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-pink-700 transition-all shadow-md flex items-center justify-center gap-2 active:scale-95"
        >
          <FaEdit size={16} />
          Edit Machine
        </button>
      </div>
    </motion.div>
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-2xl shadow overflow-hidden animate-pulse flex flex-col h-full">
      <div className="h-48 bg-gradient-to-r from-gray-100 to-gray-200" />
      <div className="p-6 space-y-4 flex-grow">
        <div className="h-7 bg-gray-200 rounded w-5/6" />
        <div className="space-y-3">
          <div className="h-5 bg-gray-200 rounded w-4/6" />
          <div className="h-5 bg-gray-200 rounded w-5/6" />
        </div>
        <div className="h-12 bg-gray-200 rounded mt-6" />
      </div>
    </div>
  );

  return (
    <>

          <div className="min-h-screen bg-gradient-to-b from-pink-50/30 to-white pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10 max-w-7xl">
          {/* Header + controls */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-12 gap-6">
            <div>
              <h1 className="text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">
                Vending Machines
              </h1>
              <p className="text-gray-600 mt-2">Manage your smart vending network</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="relative flex-1 min-w-[280px]">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="search"
                  placeholder="Search name, serial, location..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-12 pr-5 py-3.5 bg-white border border-gray-200 rounded-full shadow-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                />
              </div>

              <button
                onClick={() => openModal(null)}
                className="flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-indigo-500 to-blue-600 text-white font-semibold rounded-full shadow-lg hover:from-indigo-600 hover:to-blue-700 transition transform hover:scale-105 active:scale-95 whitespace-nowrap"
              >
                <FaPlus size={16} />
                Add Machine
              </button>
            </div>
          </div>

          {/* Grid / Loading / Empty */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {[...Array(8)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : filteredMachines.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-32"
            >
              <div className="text-9xl text-indigo-100 mb-8">🛒</div>
              <h2 className="text-3xl font-bold text-gray-700 mb-4">
                {search ? 'No matching machines found' : 'No vending machines yet'}
              </h2>
              <p className="text-lg text-gray-500 max-w-lg mx-auto">
                {search
                  ? 'Try different search terms'
                  : 'Click "Add Machine" to create your first entry'}
              </p>
            </motion.div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
              {filteredMachines.map((machine) => (
                <MachineCard
                  key={machine.vending_machine_id || machine._id || machine.serial_number}
                  machine={machine}
                />
              ))}
            </div>
          )}
        </div>

        {/* Modal - Create / Edit */}
        {modalOpen && (
          <VendingMachineModal
            machine={editMachine}
            onClose={closeModal}
            onSuccess={() => {
              fetchMachines();
              showSuccess(
                'Success!',
                editMachine
                  ? 'Vending machine updated successfully'
                  : 'New vending machine created successfully'
              );
            }}
          />
        )}
      </div>
    </>
  );
};

// ────────────────────────────────────────────────
//               Create / Edit Modal
// ────────────────────────────────────────────────
const VendingMachineModal = ({ machine, onClose, onSuccess }) => {
  const isEdit = !!machine;

  const [form, setForm] = useState(
    isEdit
      ? {
          ...machine,
          is_live: machine.is_live === true || machine.is_live === 1,
          is_active: machine.is_active === true || machine.is_active === 1,
        }
      : {
          vending_machine_name: '',
          serial_number: '',
          location: '',
          address: '',
          is_live: false,
          is_active: true,
        }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.vending_machine_name?.trim()) {
      return toast.error('Machine name is required');
    }
    if (!form.serial_number?.trim()) {
      return toast.error('Serial number is required');
    }

    const payload = {
      ...form,
      is_live: form.is_live ? 1 : 0,
      is_active: form.is_active ? 1 : 0,
    };

    if (isEdit) {
      payload.vending_machine_id = machine.vending_machine_id || machine._id;
    }

    const api = isEdit ? SummaryApi.vendingMachineUpdate : SummaryApi.vendingMachineCreate;

    try {
      const res = await fetch(api.url, {
        method: api.method || (isEdit ? 'POST' : 'POST'),
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (data.status === true) {
        onSuccess();
        onClose();
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Network error — check connection');
    }
  };

  return (
<div className="fixed inset-0 bg-black/65 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.92, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b z-10 px-7 py-5 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Vending Machine' : 'Create New Vending Machine'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FaTimes className="text-2xl text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-7 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Machine Name <span className="text-red-500">*</span>
            </label>
            <input
              required
              name="vending_machine_name"
              value={form.vending_machine_name || ''}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
              placeholder="Main Lobby Vending Machine"
            />
          </div>

          {/* Row with two input fields */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Serial Number <span className="text-red-500">*</span>
              </label>
              <input
                required
                name="serial_number"
                value={form.serial_number || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="VM-2025-00123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
              <input
                name="location"
                value={form.location || ''}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none transition-all"
                placeholder="Ground Floor, Building A"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Address</label>
            <textarea
              name="address"
              value={form.address || ''}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none resize-none transition-all"
              placeholder="123 Tech Park Road, Vijayawada, Andhra Pradesh - 520010"
            />
          </div>

          <div className="grid grid-cols-2 gap-6 pt-2">
            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                name="is_live"
                checked={form.is_live}
                onChange={handleChange}
                className="sr-only"
              />
              <div
                className={`w-12 h-6 rounded-full transition-colors duration-300 ${
                  form.is_live ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                    form.is_live ? 'translate-x-6' : 'translate-x-1'
                  } mt-0.5`}
                />
              </div>
              <span className="font-medium text-gray-700">Live / Online</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="sr-only"
              />
              <div
                className={`w-12 h-6 rounded-full transition-colors duration-300 ${
                  form.is_active ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-300 ${
                    form.is_active ? 'translate-x-6' : 'translate-x-1'
                  } mt-0.5`}
                />
              </div>
              <span className="font-medium text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end gap-4 pt-6 border-t mt-4">
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-pink-500 to-pink-600 text-white font-bold rounded-xl hover:from-pink-600 hover:to-pink-700 transition shadow-md"
            >
              {isEdit ? 'Update Machine' : 'Create Machine'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AllVendingMachines;